import React, { useMemo, forwardRef } from "react";
import plus from "@assets/icon_add.svg";
import extend from "@assets/calendar.svg";
import { MilestoneDisplay } from "@shared/interfaces/ProgramFiles";
import useResponsiveColumnWidth from "@pages/Program/components/calendar/utils/useResponsiveColumnWidth"; // adjust path

import {
  format,
  addDays,
  addMonths,
  endOfMonth,
  differenceInCalendarDays,
  isBefore,
  isAfter,
  isWithinInterval,
} from "date-fns";
import WeekHeader from "./components/WeekHeader";
import MonthHeader from "./components/MonthHeader";
import TodayMarker from "./components/TodayMarker";
import TimelineBackground from "./components/TimelineBackground";
import EventList from "./components/EventList";
import { Tooltip } from "flowbite-react";

interface CalendarTimelineProps {
  events: MilestoneDisplay[];
  msViewStart: Date;
  setMsViewStart: React.Dispatch<React.SetStateAction<Date>>;
  msViewEnd: Date;
  openMilestonePopup: () => void;
  openCalendar: () => void;
  fetchMilestone: () => void;
  startDate?: string;
  endDate?: string;
}

const CalendarTimeline = forwardRef<HTMLDivElement, CalendarTimelineProps>(
  (
    {
      events,
      msViewStart,
      setMsViewStart,
      msViewEnd,
      openCalendar,
      fetchMilestone,
      startDate,
      endDate,
    },
    ref
  ) => {
    const MONTHS_TO_SHOW = 2;
    const EVENT_HEIGHT_FULL = 78;
    const EVENT_HEIGHT_COMPACT = 30;

    const weekDates = useMemo(() => {
      const weeks = [];
      let current = msViewStart;

      // calculate the end of N months
      const finalDate = endOfMonth(addMonths(msViewStart, MONTHS_TO_SHOW - 1));

      // ✅ Add one extra week to always show the partial next month
      const finalDateWithExtraWeek = addDays(finalDate, 7);

      while (current <= finalDateWithExtraWeek) {
        weeks.push(current);
        current = addDays(current, 7);
      }

      return weeks;
    }, [msViewStart]);

    const weeksPerView = weekDates.length;
    const { columnWidth: COLUMN_WIDTH, containerRef } =
      useResponsiveColumnWidth(weeksPerView);

    const visibleEvents = useMemo(
      () =>
        events.filter(
          (event) =>
            isWithinInterval(event.start_date, {
              start: msViewStart,
              end: msViewEnd,
            }) ||
            isWithinInterval(event.end_date, {
              start: msViewStart,
              end: msViewEnd,
            }) ||
            (isBefore(event.start_date, msViewStart) &&
              isAfter(event.end_date, msViewEnd))
        ),
      [msViewStart, msViewEnd, events]
    );

    const eventHeight =
      visibleEvents.length > 10 ? EVENT_HEIGHT_COMPACT : EVENT_HEIGHT_FULL;

    const goPrevious = () =>
      setMsViewStart((prev) => addMonths(prev, -MONTHS_TO_SHOW));
    const goNext = () =>
      setMsViewStart((prev) => addMonths(prev, MONTHS_TO_SHOW));

    // ✅ [NEW] Today's line logic
    const today = new Date();
    const showTodayLine = isWithinInterval(today, {
      start: msViewStart,
      end: msViewEnd,
    });
    const todayOffsetDays = differenceInCalendarDays(today, msViewStart);
    const todayLeft = todayOffsetDays * (COLUMN_WIDTH / 7);

    return (
      <div className="flex flex-col justify-betweeny" ref={ref}>
        {/* Controls */}
        <div className="flex flex-row items-center py-4 border-t border-x border-gray-200 rounded-t-lg">
          <div className="flex-1 flex justify-between items-center">
            <div className="w-24"></div>
            <div className="flex justify-center space-x-4 px-3 items-center">
              <button
                onClick={goPrevious}
                className="py-1.5 px-1 rounded hover:bg-gray-100 text-xs"
              >
                <img src="/back-arrow.svg" className="w-4 h-2.5" alt="" />
              </button>
              <div className="text-sm 2xl:text-base font-semibold text-gray-700">
                {format(msViewStart, "MMM yyyy")} –{" "}
                {format(msViewEnd, "MMM yyyy")}
              </div>
              <button
                onClick={goNext}
                className="py-1.5 px-1 rounded hover:bg-gray-100 text-xs"
              >
                <img src="/forward-arrow.svg" className=" w-4 h-2.5" alt="" />
              </button>
            </div>
            <div className="flex w-24 items-center justify-end space-x-6 pr-4 text-gray-500">
              <Tooltip content="View Calendar" placement="top" arrow={false}>
                <img
                  src={extend}
                  className="w-3 h-4 2xl:w-5 2xl:h-5 cursor-pointer"
                  onClick={openCalendar}
                  alt=""
                />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col h-full  w-full ">
          {/* Calendar Grid */}
          <div
            className="overflow-x-auto border rounded-b-lg bg-white"
            ref={containerRef}
          >
            <div className="w-full flex items-center ">
              <div className="overflow-y-hidden overflow-x-hidden">
                {/* Month Header */}
                <MonthHeader weekDates={weekDates} columnWidth={COLUMN_WIDTH} />

                {/* Week Ranges */}
                <WeekHeader weekDates={weekDates} columnWidth={COLUMN_WIDTH} />

                {/* Events */}
                <div
                  className="relative overflow-x-hidden overflow-y-auto scrollbar-hide rounded-b-lg min-h-[280px] max-h-[280px] 2xl:min-h-[320px] 2xl:max-h-[320px]"
                  style={{
                    height: `${visibleEvents.length * eventHeight + 120}px`,
                  }}
                >
                  <TodayMarker
                    show={showTodayLine}
                    left={todayLeft}
                    height={visibleEvents.length * eventHeight + 100}
                  />

                  <TimelineBackground
                    weekDates={weekDates}
                    columnWidth={COLUMN_WIDTH}
                    height={visibleEvents.length * eventHeight + 120}
                  />

                  <EventList
                    events={visibleEvents}
                    msViewStart={msViewStart}
                    weeksPerView={weeksPerView}
                    columnWidth={COLUMN_WIDTH}
                    eventHeight={eventHeight}
                    fetchMilestone={fetchMilestone}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default CalendarTimeline;
